import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box } from "@caspeco/casper-ui-library.components.box";
import {
	ThemeColorVariable,
	ThemeFontVariable,
	ThemeSpaceVariable,
} from "@caspeco/casper-ui-library.base-ui.theme";
import useKeypress from "hooks/useKeypress";
import { CartRow } from "types/cart";
import { Flex } from "@caspeco/casper-ui-library.components.flex";
import { QuantityButton } from "@caspeco/casper-ui-library.components.quantity-button";
import { useCartAPI, useCartStore } from "store/cartStore";
import { IconButton } from "@caspeco/casper-ui-library.components.icon-button";
import { Icons } from "@caspeco/casper-ui-library.components.icon";
import { useDisclosure } from "@caspeco/casper-ui-library.components.modal";
import ConfirmationDialog from "components/confirm-dialog/ConfirmDialog";
import { logInfo } from "helpers/log-helper/log-helper";
import { LogPrefix } from "types/logging";
import { useToast } from "@caspeco/casper-ui-library.components.toast";
import { AbortAction, ApiService } from "services/Api.service";
import Price from "components/price/Price";
import { BSTLApiError } from "types/api-error";
import { StyledSingleCartRow } from "./SingleCartRow.styles";
import CartRowContent from "./subcomponents/CartRowContent";
import CartRowAddons from "./subcomponents/CartRowAddons";

interface ICartRow {
	row: CartRow;
	onClickCartRow: (row: CartRow) => void;
	borderBottom: boolean;
}

function SingleCartRow({ row, onClickCartRow, borderBottom }: ICartRow) {
	const { article, totalAmountForRow } = row;
	const { name, optionGroups, extraGroups, imageMap } = article;
	const { cart } = useCartStore();
	const [quantityIsChanging, setQuantityIsChanging] = useState(false);

	const handleClick = () => {
		if (quantityIsChanging) {
			return;
		}
		onClickCartRow(row);
	};
	const singleCartRowRef = useRef<HTMLDivElement | null>(null);
	const { deleteCartRow } = useCartAPI();

	const { isOpen, onClose, onOpen } = useDisclosure();
	const { t } = useTranslation();
	const { showToast } = useToast();

	// For keyboard navigation purpose - listen to spacebar keypress
	useKeypress(32, () => {
		if (document.activeElement === singleCartRowRef?.current) {
			// User has focused this cart row and pressed spacebar,
			// this should equal a click action on the cart row
			handleClick();
		}
	});

	const numberOfAddons = optionGroups.length + extraGroups.length;
	const addonsElment =
		numberOfAddons > 0 ? <CartRowAddons extraGroups={extraGroups} optionsGroups={optionGroups} /> : null;

	const onDelete = async () => {
		try {
			if (!cart) {
				return;
			}

			await deleteCartRow(cart.cartId, row.id);

			logInfo(LogPrefix.Cart, `Deleted Article from Cart, Name: ${article.name}, Id: ${article.id}`);
		} catch {
			showToast({
				type: "error",
				description: t("error_delete_article"),
			});
		}
	};

	return (
		<StyledSingleCartRow $borderBottom={borderBottom}>
			<Box
				ref={singleCartRowRef}
				display="flex"
				w="100%"
				h="100%"
				className="cart-row-content"
				onClick={handleClick}
				tabIndex={0}
				cursor="pointer"
			>
				<Box w="100%">
					<Box display="flex" justifyContent="space-between" w="100%">
						<CartRowContent name={name} addonsElement={addonsElment} imageMap={imageMap} />

						<Price
							inHousePrice={totalAmountForRow}
							color={ThemeColorVariable.OnSurface}
							fontWeight="medium"
							fontFamily={ThemeFontVariable.SubHeader}
						/>
					</Box>
				</Box>
			</Box>
			<Flex marginTop={ThemeSpaceVariable.Small} gap={ThemeSpaceVariable.Medium} alignItems="center">
				<ChangeQuantity
					row={row}
					setQuantityIsChanging={(isChanging) => {
						setQuantityIsChanging(isChanging);
					}}
				/>
				<IconButton icon={Icons.Delete} aria-label="delete" type="button" variant="ghost" onClick={onOpen} />
			</Flex>
			<ConfirmationDialog
				isOpen={isOpen}
				onClose={onClose}
				primaryAction={onDelete}
				primaryButtonText={t("action_remove")}
				title={t("article_remove_article")}
				message={t("article_do_you_want_to_remove_x", { 0: name })}
				size="md"
			/>
		</StyledSingleCartRow>
	);
}

const ChangeQuantity = ({
	row,
	setQuantityIsChanging,
}: {
	row: CartRow;
	setQuantityIsChanging: (isChanging: boolean) => void;
}) => {
	const { article, quantity } = row;

	const { t } = useTranslation();
	const [newQuantity, setNewQuantity] = useState(quantity);
	const [quantityInCart, setQuantityInCart] = useState(quantity);
	const { updateCartRowQuantity } = useCartAPI();
	const { cart, isLoadingCart } = useCartStore();
	const { showToast } = useToast();

	useEffect(() => {
		setNewQuantity(quantity);
		setQuantityInCart(quantity);
	}, [quantity]);

	const timeOut = useRef<NodeJS.Timeout | null>(null);

	const handleUpdateCartRow = (updateQuantity: number) => {
		setQuantityIsChanging(true);

		if (timeOut.current) {
			clearTimeout(timeOut.current);
		}

		if (!cart) {
			return;
		}

		setNewQuantity(updateQuantity);

		timeOut.current = setTimeout(async () => {
			try {
				ApiService.cancelRequest(AbortAction.UPDATE_CART_ROW_QUANTITY);

				await updateCartRowQuantity(cart.cartId, row.id, updateQuantity);
				setQuantityInCart(updateQuantity);

				setQuantityIsChanging(false);

				logInfo(
					LogPrefix.Cart,
					`Updated existing Article in Cart, Name: ${article.name}, Id: ${article.id}, Quantity: ${updateQuantity}`,
				);
			} catch (error) {
				setQuantityIsChanging(false);

				if (error instanceof Error && error.name === "AbortError") {
					return;
				}

				setNewQuantity(quantityInCart);

				const message: string =
					error instanceof BSTLApiError ? error.translatedMessage : t("error_update_cart");

				showToast({
					type: "error",
					description: message,
				});
			}
		}, 400);
	};

	return (
		<QuantityButton
			onClickMinus={() => {
				if (newQuantity > 1) {
					handleUpdateCartRow(newQuantity - 1);
				}
			}}
			onClickPlus={() => {
				handleUpdateCartRow(newQuantity + 1);
			}}
			quantity={newQuantity}
			minusButtonAriaLabel={t("action_decrease_quantity")}
			plusButtonAriaLabel={t("action_increase_quantity")}
			min={1}
			max={99}
			disabled={isLoadingCart}
		/>
	);
};

export default SingleCartRow;
